import React, { useContext, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import uuid from 'uuid';
import { styled } from '@mui/material';
import { LogoLoading } from 'ensightful-koala-ui';

import { Button, MenuItem, ResponsiveDialog, Stack, Text, TextArea, Tooltip } from 'shell/components';
import { PageContext } from 'pages/PeerEvaluation/PageProvider';
import { useViewport } from 'contexts/viewport';
import { useSnackbar } from 'contexts/snackbar';
import courseService from 'services/course';
import { UploadResourcesModal } from '../Task';
import { ScheduleModal } from '../TaskTemplates';
import StudentEvaluation from 'components/organisms/PeerEvaluation/StudentEvaluation';
import CreatePeerEvaluationDesktopTablet from 'components/organisms/PeerEvaluation/CreatePeerEvaluationDesktopTablet';
import CreatePeerEvaluationMobile from 'components/organisms/PeerEvaluation/CreatePeerEvaluationMobile';
import { useAuth } from 'contexts/auth';

const FORM_KEYS = {
    TITLE: 'title',
    LTI_ASSIGNMENT: 'lti_assignment',
    FORMAT: 'format',
    START_ON: 'start_on',
    END_ON: 'end_on',
    DUE_ON: 'due_on',
    DETAILS: 'details',
    IS_COMMENT_REQURIED: 'is_comment_required',
    RELEASE_ON: 'release_on',
    MIN_FEEDBACK_LENGTH: 'min_feedback_length',
    RELEASE_TO_STUDENTS: 'release_to_students',
    KEEP_RESULT_ANONYMOUS: 'keep_result_anonymous',
    RESOURCES: 'resources',
    SCHEDULE_ON: 'schedule_on',
    RUBRIC_DATA: 'rubric_data',
    COMMENT: 'comment', // Tooltip text
};

const getCreatedOnText = (peerEvaluation) => {
    switch (peerEvaluation.status) {
        case 'published': {
            if (peerEvaluation.modified_on > peerEvaluation.created_on) {
                return `Published on ${moment(peerEvaluation.modified_on).format('MMMM D[,] YYYY')}`;
            }
            return `Published on ${moment(peerEvaluation.created_on).format('MMMM D[,] YYYY')}`;
        }
        case 'draft': {
            if (!peerEvaluation.created_on) {
                return '';
            }
            return `Draft was created on ${moment(peerEvaluation.created_on).format('MMMM D[,] YYYY')}`;
        }
        case 'scheduled': {
            return `Scheduled to post ${moment(peerEvaluation.schedule_on).format('MMMM D[,] YYYY')}`;
        }
    }
    return '';
};

const getSplitButtons = (peerEvaluation, disabled) => {
    if (peerEvaluation.status !== 'draft') {
        return [{ key: 'save', value: 'Save', disabled: disabled }];
    } else {
        return [
            { key: 'save', value: 'Save', disabled: disabled },
            { key: 'publish', value: 'Publish', disabled: false },
            { key: 'schedule', value: 'Schedule', disabled: false },
        ];
    }
};

const renderOptionDropDown = (device, peerEvaluation) => {
    if (device === 'mobile') {
        if (peerEvaluation.status === 'published') {
            return [
                { key: 'view-as-student', value: 'View as Student' },
                { key: 'unpublish', value: 'Unpublish' },
                { key: 'delete', value: 'Delete' },
            ];
        }
        return [
            { key: 'view-as-student', value: 'View as Student' },
            { key: 'delete', value: 'Delete' },
        ];
    }
    if (peerEvaluation.status === 'published') {
        return [
            { key: 'unpublish', value: 'Unpublish' },
            { key: 'delete', value: 'Delete' },
        ];
    }
    return [{ key: 'delete', value: 'Delete' }];
};

const getRubricErrors = (data) => {
    const reduceErrors = data?.reduce((errors, criterion) => {
        let criterionErrors = {};
        if (!criterion.title) {
            criterionErrors = {
                title: true,
            };
        }
        if (!criterion.description) {
            criterionErrors = {
                ...criterionErrors,
                description: true,
            };
        }
        const findErrors = criterion.options?.reduce((optionErrors, option) => {
            const rate = parseInt(option.rate);
            if (rate < 0 || isNaN(rate)) {
                optionErrors = {
                    ...optionErrors,
                    [option.id]: { ...optionErrors?.[option.id], rate: 'Points are required.' },
                };
            }
            return optionErrors;
        }, {});
        errors = { ...errors, [criterion.id]: { ...criterionErrors, ...findErrors } };
        return errors;
    }, {});
    return reduceErrors;
};

const CreatePeerEvaluation = ({}) => {
    const { isLTI } = useAuth();
    const { message } = useSnackbar();
    const { device } = useViewport();
    const {
        auth,
        buttonLoading,
        setButtonLoading,

        initialPeerEvaluationState,
        form,
        setForm,
        rubricData,
        setRubricData,
        handleOpenDialog,
        currentPeerEvaluationId,
        setCurrentPeerEvaluationId,
        currentPeerEvaluation,
        isViewingSubmission,
        setInstructorPage,
        peerEvaluationList,
        submissions,
        createPeerEvaluation,
        updatePeerEvaluation,

        //LTI
        LTIAssignments,
        listLTIAssignments,

        //constants
        DIALOG,
        INSTRUCTOR_PAGES,
    } = useContext(PageContext);
    const tooltipInputRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [tooltipDialog, setToolTipDialog] = useState({ open: false });
    const [scheduleModal, setScheduleModal] = useState({ visible: false });
    const [resourcesModal, setResourcesModal] = useState({ isOpen: false });
    const [isPreviewing, setIsPreviewing] = useState(false);
    const [hasChange, setHasChange] = useState(false);

    //Errors
    const [errors, setErrors] = useState({
        [FORM_KEYS.TITLE]: false,
        [FORM_KEYS.FORMAT]: false,
        [FORM_KEYS.START_ON]: false,
        [FORM_KEYS.END_ON]: false,
        [FORM_KEYS.DUE_ON]: false,
        [FORM_KEYS.DETAILS]: false,
        [FORM_KEYS.IS_COMMENT_REQURIED]: false,
        [FORM_KEYS.RELEASE_ON]: false,
        [FORM_KEYS.MIN_FEEDBACK_LENGTH]: false,
        [FORM_KEYS.RUBRIC_DATA]: getRubricErrors(rubricData),
    });

    const handleValidation = () => {
        const requiredFields = [
            FORM_KEYS.TITLE,
            FORM_KEYS.FORMAT,
            FORM_KEYS.START_ON,
            FORM_KEYS.END_ON,
            FORM_KEYS.DUE_ON,
        ];
        let count = 0;
        requiredFields.map((key) => {
            if (!form?.[key]) {
                setErrors((current) => ({ ...current, [key]: true }));
                count = count + 1;
            }
        });
        if (count > 0) {
            message.error('Please check your inputs.');
            return false;
        }
        if (form?.[FORM_KEYS.START_ON] > form?.[FORM_KEYS.END_ON]) {
            setErrors((current) => ({ ...current, [FORM_KEYS.START_ON]: true, [FORM_KEYS.END_ON]: true }));
            message.error('Please check your selected dates.');
            return false;
        } else if (form?.[FORM_KEYS.DUE_ON] > form?.[FORM_KEYS.END_ON]) {
            setErrors((current) => ({
                ...current,
                [FORM_KEYS.START_ON]: false,
                [FORM_KEYS.DUE_ON]: true,
                [FORM_KEYS.END_ON]: true,
            }));
            message.error('Please check your selected dates.');
            return false;
        } else if (form?.[FORM_KEYS.START_ON] > form?.[FORM_KEYS.DUE_ON]) {
            setErrors((current) => ({
                ...current,
                [FORM_KEYS.START_ON]: true,
                [FORM_KEYS.DUE_ON]: true,
                [FORM_KEYS.END_ON]: false,
            }));
            message.error('Please check your selected dates.');
            return false;
        } else if (form?.[FORM_KEYS.RELEASE_TO_STUDENTS] && form?.[FORM_KEYS.END_ON] > form?.[FORM_KEYS.RELEASE_ON]) {
            setErrors((current) => ({
                ...current,
                [FORM_KEYS.START_ON]: false,
                [FORM_KEYS.DUE_ON]: false,
                [FORM_KEYS.END_ON]: true,
                [FORM_KEYS.RELEASE_ON]: true,
            }));
            message.error('Please check your release date.');
            return false;
        }
        if (form?.[FORM_KEYS.IS_COMMENT_REQURIED] && !form?.[FORM_KEYS.MIN_FEEDBACK_LENGTH]) {
            setErrors((current) => ({
                ...current,
                [FORM_KEYS.MIN_FEEDBACK_LENGTH]: true,
            }));
            message.error('Please complete all required fields.');
            return false;
        }

        if (!form?.[FORM_KEYS.FORMAT]) {
            setErrors((current) => ({
                ...current,
                [FORM_KEYS.START_ON]: false,
                [FORM_KEYS.DUE_ON]: false,
                [FORM_KEYS.END_ON]: false,
                [FORM_KEYS.RELEASE_ON]: false,
                [FORM_KEYS.FORMAT]: true,
            }));
            message.error('Please select format.');
            return false;
        }
        if (
            form[FORM_KEYS.FORMAT] === 'rubric' &&
            Object.values(errors[FORM_KEYS.RUBRIC_DATA]).find((value) => {
                return Object.values(value).some((value) => value.rate);
            })
        ) {
            message.error('Please correct rates.');
            return false;
        }
        return true;
    };

    const handleInputs = (key, value) => {
        switch (key) {
            case FORM_KEYS.MIN_FEEDBACK_LENGTH: {
                if (Number.isInteger(Number(value)) && Number(value) >= 0) {
                    setHasChange(true);
                    setErrors((current) => ({ ...current, [FORM_KEYS.MIN_FEEDBACK_LENGTH]: false }));
                    return setForm((form) => ({ ...form, [key]: value }));
                }
                return setErrors((current) => ({ ...current, [FORM_KEYS.MIN_FEEDBACK_LENGTH]: true }));
            }
            default: {
                setHasChange(true);
                return setForm((form) => ({ ...form, [key]: value }));
            }
        }
    };

    const renderLTIAssignmentsMenuItem = () => {
        return LTIAssignments?.map((assignment) => {
            return <MenuItem value={assignment.resourceLinkId}>{assignment.label}</MenuItem>;
        });
    };

    const handleSplitButton = ({ key }) => {
        if (!handleValidation()) return;
        setHasChange(false);
        if (form?.format === 'rubric') {
            form.rubric_data = rubricData;
        }
        switch (key) {
            case 'publish': {
                const data = {
                    ...form,
                    status: 'published',
                };
                if (peerEvaluationList.find((peerEvaluation) => form.id === peerEvaluation.id)) {
                    return updatePeerEvaluation(data);
                }
                return createPeerEvaluation(data);
            }
            case 'save': {
                if (peerEvaluationList.find((peerEvaluation) => form.id === peerEvaluation.id)) {
                    return updatePeerEvaluation(form);
                }
                const data = {
                    ...form,
                    status: 'draft',
                };
                return createPeerEvaluation(data);
            }
            case 'schedule': {
                return setScheduleModal({ visible: true });
            }
        }
    };

    const handlePreview = () => {
        if (!form?.[FORM_KEYS.FORMAT]) return message.error('Please select a format.');
        return setIsPreviewing((open) => !open);
    };

    const handleAddResources = (files, link) => {
        let resources = form?.[FORM_KEYS.RESOURCES] || [];
        const fileResources = files.map((file) => ({ data: file, type: 'file' }));
        const linkResource = { ...link, type: 'link' };
        resources = resources.concat(fileResources);
        if (linkResource.url && linkResource.name) resources = resources.concat(linkResource);
        resources = resources.map((resource) => ({
            ...resource,
            active: true,
            id: uuid.v4(),
            owner_account_id: auth.owner_account_id,
            peer_evaluation_id: form?.id,
        }));
        handleInputs(FORM_KEYS.RESOURCES, resources);
        setResourcesModal((current) => ({ ...current, isOpen: false }));
    };

    const handleDeleteFile = (id) => {
        const removeFile = form?.[FORM_KEYS.RESOURCES]?.filter((resource) => resource.id !== id);
        handleInputs(FORM_KEYS.RESOURCES, removeFile);
    };

    const setScheduleTime = async (timestamp) => {
        handleInputs(FORM_KEYS.SCHEDULE_ON, timestamp);
        setScheduleModal({ visible: false });
        const data = {
            ...form,
            status: 'scheduled',
        };
        if (peerEvaluationList.find((peerEvaluation) => form.id === peerEvaluation.id)) {
            return updatePeerEvaluation(data);
        }
        return createPeerEvaluation(data);
    };

    const handleDropDown = ({ key }) => {
        switch (key) {
            case 'unpublish': {
                return handleOpenDialog(DIALOG.PEER_EVALUATION_UNPUBLISH, { id: currentPeerEvaluationId });
            }
            case 'delete': {
                return handleOpenDialog(DIALOG.PEER_EVALUATION_DELETE, { id: currentPeerEvaluationId });
            }
            case 'view-as-student': {
                return handlePreview();
            }
            default:
                return;
        }
    };

    const handleSaveToolTipComment = () => {
        if (tooltipInputRef?.current?.value) {
            handleInputs(FORM_KEYS.COMMENT, tooltipInputRef?.current?.value);
        }
        setToolTipDialog({ open: false });
    };

    const handleBackButton = () => {
        if (hasChange) {
            return handleOpenDialog(DIALOG.PEER_EVALUATION_CANCEL);
        } else {
            if (isViewingSubmission) {
                setInstructorPage(INSTRUCTOR_PAGES.SUBMISSIONS);
            } else {
                setInstructorPage(INSTRUCTOR_PAGES.TABLE);
                setCurrentPeerEvaluationId(null);
            }
        }
    };

    useEffect(() => {
        if (currentPeerEvaluationId) {
            if (currentPeerEvaluation) {
                setHasChange(false);
                const convertToForm = {
                    ...currentPeerEvaluation,
                    format: currentPeerEvaluation?.format?.value || null,
                    start_on: currentPeerEvaluation.start_on ? moment(currentPeerEvaluation.start_on) : null,
                    end_on: currentPeerEvaluation.end_on ? moment(currentPeerEvaluation.end_on) : null,
                    due_on: currentPeerEvaluation.end_on ? moment(currentPeerEvaluation.due_on) : null,
                    release_on: currentPeerEvaluation.release_on ? moment(currentPeerEvaluation.release_on) : null,
                };
                if (convertToForm.format === 'rubric') {
                    setRubricData(currentPeerEvaluation.rubric_data);
                }
                if (currentPeerEvaluation?.resources) {
                    setLoading(true);
                    (async () => {
                        if (isLTI) {
                            const ltiAssignments = await listLTIAssignments();
                            if (currentPeerEvaluation.lti_assignment) {
                                const selectedLTIAssignment = ltiAssignments?.find(
                                    (assignment) =>
                                        assignment.resourceLinkId ===
                                        currentPeerEvaluation?.lti_assignment?.value?.resourceLinkId
                                );
                                convertToForm.lti_assignment = selectedLTIAssignment.resourceLinkId || null;
                            }
                        }
                        await new Promise(async (resolve, reject) => {
                            const fileIds = currentPeerEvaluation?.resources
                                ?.filter((resource) => resource.type === 'file')
                                .map((resource) => resource.id);
                            const urlIds = currentPeerEvaluation?.resources
                                ?.filter((resource) => resource.type === 'url')
                                .map((resource) => resource.id);
                            const files = await courseService.listCourseFiles(
                                auth.course_id,
                                JSON.stringify({
                                    type: 'peer-evaluation',
                                    id: { $in: [...fileIds] },
                                })
                            );

                            const sharedUrls = await courseService.listCourseSharedUrls(
                                auth.course_id,
                                JSON.stringify({
                                    type: 'peer-evaluation',
                                    id: { $in: [...urlIds] },
                                })
                            );
                            convertToForm.resources = [
                                ...files.map((file) => ({ ...file, type: 'file' })),
                                ...sharedUrls.map((url) => ({ ...url, type: 'link' })),
                            ];
                            setForm(convertToForm);
                            setLoading(false);
                            resolve();
                        });
                    })();
                }
            }
        } else {
            setForm(initialPeerEvaluationState);
        }
    }, [currentPeerEvaluationId, currentPeerEvaluation, peerEvaluationList]);

    useEffect(() => {
        setErrors((currentErrors) => ({ ...currentErrors, [FORM_KEYS.RUBRIC_DATA]: getRubricErrors(rubricData) }));
    }, [rubricData]);

    if (loading) return <LogoLoading />;

    if (isPreviewing) return <StudentEvaluation demo={true} handleBack={handlePreview} />;

    const props = {
        auth: auth,
        buttonLoading,
        setButtonLoading,
        form: form,
        errors: errors,
        rubricData: rubricData,
        submissions,
        hasChange,
        setHasChange,

        //LTI
        renderLTIAssignmentsMenuItem,

        setRubricData: setRubricData,
        setResourcesModal: setResourcesModal,
        getSplitButtons: getSplitButtons,
        getCreatedOnText,
        setToolTipDialog: setToolTipDialog,
        renderOptionDropDown: renderOptionDropDown,
        setScheduleTime: setScheduleTime,

        handleAddResources: handleAddResources,
        handleDeleteFile: handleDeleteFile,
        handleDropDown: handleDropDown,
        handleInputs: handleInputs,
        handleOpenDialog: handleOpenDialog,
        handleBackButton,
        handleSaveToolTipComment: handleSaveToolTipComment,
        handlePreview: handlePreview,
        handleSplitButton: handleSplitButton,

        // Modals
        resourcesModal: resourcesModal,
        scheduleModal: scheduleModal,
        setScheduleModal: setScheduleModal,
        tooltipDialog: tooltipDialog,

        DIALOG: DIALOG,
        FORM_KEYS: FORM_KEYS,
    };

    return (
        <>
            {device === 'mobile' ? (
                <CreatePeerEvaluationMobile {...props} />
            ) : (
                <CreatePeerEvaluationDesktopTablet {...props} />
            )}
            <UploadResourcesModal
                {...resourcesModal}
                device={device}
                submitResources={handleAddResources}
                onClose={() => setResourcesModal((current) => ({ ...current, isOpen: false }))}
            />
            <ScheduleModal
                visible={scheduleModal.visible}
                onSchedule={(timestamp) => setScheduleTime(timestamp)}
                onCancel={() => setScheduleModal({ visible: false })}
            />
            <ResponsiveDialog
                open={tooltipDialog.open}
                onCancel={() => setToolTipDialog({ open: false })}
                onClose={() => setToolTipDialog({ open: false })}
            >
                <ResponsiveStack device={device} spacing={7}>
                    <Text variant={'h6'}>Customize Tooltip for Students</Text>
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        <Text variant={'p2'}>Tips for students to write their evaluation</Text>
                        <Tooltip
                            maxWidth={160}
                            title={
                                <Stack spacing={2}>
                                    <Stack>
                                        <Text color={'inherit'} variant={'p3'}>
                                            Provide students with guidelines about what they should cover in their
                                            evaluation to help them write effective feedback for their peers. Leave this
                                            blank and we will display the following default message:
                                        </Text>
                                        <Text color={'inherit'} variant={'p3'}>
                                            “Tips: Be honest, but professional. Provide detailed explanations of areas
                                            that your teammate did well in and areas they can improve. Explain how
                                            improving this area can benefit the individual.”
                                        </Text>
                                    </Stack>
                                </Stack>
                            }
                            type={'info'}
                        />
                    </Stack>
                    <Stack spacing={4}>
                        <TextArea
                            ref={tooltipInputRef}
                            error={errors[FORM_KEYS.COMMENT]}
                            value={form?.[FORM_KEYS.COMMENT]}
                            placeholder={'Optional'}
                            rows={5}
                        />
                        <Stack direction={'row'} justifyContent={'flex-end'} spacing={9}>
                            <Button variant={'tertiary'} onClick={() => setToolTipDialog({ open: false })}>
                                Cancel
                            </Button>
                            <Button
                                disabled={tooltipInputRef?.current?.value?.length < 0}
                                variant={'primary'}
                                onClick={handleSaveToolTipComment}
                            >
                                Save Changes
                            </Button>
                        </Stack>
                    </Stack>
                </ResponsiveStack>
            </ResponsiveDialog>
        </>
    );
};

const ResponsiveStack = styled(Stack)(({ device, theme }) => {
    return {
        width: device === 'desktop' ? theme.spacing(168) : '100%',
        height: device === 'mobile' ? '100%' : 'inherit',
    };
});

export default CreatePeerEvaluation;
